import { useMemo } from 'react';

import { useAnnotationAcknowledgmentStore } from 'stores/annotationAcknowledgments/annotationAcknowledgments';

import { scrollToNavigableItem } from '../schemableFormNavigation/navigationHelpers';
import { isItemPending, NavigationService } from '../schemableFormNavigation/schemableFormNavigation';
import { WebEDIAnnotation } from './types';

const NO_ITEMS: ReadonlyArray<WebEDIAnnotation> = [];

export class WebEDIErrorsService extends NavigationService<WebEDIAnnotation> {
  constructor() {
    super();
  }

  getFirstUnresolvedAnnotation = (): WebEDIAnnotation | undefined => {
    const acknowledgedIds = useAnnotationAcknowledgmentStore.getState().acknowledgedIds;
    return this.store
      .getState()
      .items.find((item) => item.severity === 'error' && 'code' in item && !acknowledgedIds.has(item.id));
  };

  focusOnCurrentItem = async (): Promise<void> => {
    const currentError = this.getCurrentItem();

    if (currentError) {
      return scrollToNavigableItem(currentError, this.scrollToVirtualizedRow, this.containerRef);
    }
    return scrollToNavigableItem(this.store.getState().items[0], this.scrollToVirtualizedRow, this.containerRef);
  };

  useActiveItems = (): Array<WebEDIAnnotation> => {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- class-field arrow assigned with a `use*` name is a custom hook; ESLint can't see that through the class boundary.
    const acknowledgedIds = useAnnotationAcknowledgmentStore((state) => state.acknowledgedIds);
    return this.store((state) =>
      state.items.filter((item) => !isItemPending(item, state.pendingFieldPaths) && !acknowledgedIds.has(item.id)),
    );
  };

  useItemsByFieldPath = (fieldPath: string): ReadonlyArray<WebEDIAnnotation> => {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- class-field arrow assigned with a `use*` name is a custom hook; ESLint can't see that through the class boundary.
    const acknowledgedIds = useAnnotationAcknowledgmentStore((state) => state.acknowledgedIds);
    const items = this.store((state) => state.itemsByFieldPath[fieldPath] ?? NO_ITEMS);
    // Filtering inside the store selector would return a new array on every store write, which
    // re-renders every subscribed cell even when nothing at this path changed.
    // eslint-disable-next-line react-hooks/rules-of-hooks -- class-field arrow assigned with a `use*` name is a custom hook; ESLint can't see that through the class boundary.
    return useMemo(
      () => (acknowledgedIds.size === 0 ? items : items.filter((item) => !acknowledgedIds.has(item.id))),
      [items, acknowledgedIds],
    );
  };

  useNumberOfErrors = (): number => {
    const items = this.useActiveItems();
    let count = 0;
    for (const item of items) {
      if (item.severity === 'warning') {
        continue;
      }
      count += 1;
    }
    return count;
  };

  useNumberOfWarnings = (): number => {
    const items = this.useActiveItems();
    let count = 0;
    for (const item of items) {
      if (item.severity !== 'warning') {
        continue;
      }
      count += 1;
    }
    return count;
  };
}
